import { computed } from "vue";
import { logEvent } from "~/utils/firebase";
import { useI18n } from "~/composables/useI18n";

const PAGE_NAME = "service_institution";
const SESSION_KEY = "seer_service_institution_session_id";

function getSessionId() {
	if (!import.meta.client) return "";

	try {
		const cached = sessionStorage.getItem(SESSION_KEY);
		if (cached) return cached;

		const id =
			typeof crypto !== "undefined" && crypto.randomUUID
				? crypto.randomUUID()
				: `${Date.now()}-${Math.random().toString(36).slice(2)}`;
		sessionStorage.setItem(SESSION_KEY, id);
		return id;
	} catch {
		return "";
	}
}

function getEntrySource() {
	if (!import.meta.client) return "direct";
	const referrer = document.referrer;
	if (!referrer) return "direct";

	try {
		const referrerUrl = new URL(referrer);
		if (referrerUrl.host === window.location.host) return "internal";
		return referrerUrl.host;
	} catch {
		return "unknown";
	}
}

function getLoginStatus() {
	if (!import.meta.client) return "guest";
	return localStorage.getItem("token") ? "logged_in" : "guest";
}

function getUserId() {
	if (!import.meta.client) return "";

	try {
		const user = JSON.parse(localStorage.getItem("gwUser") || "{}");
		return user.user_id || user.uid || user.id || "";
	} catch {
		return "";
	}
}

function cleanParams(params) {
	return Object.fromEntries(
		Object.entries(params).filter(
			([, value]) => value !== undefined && value !== null && value !== ""
		)
	);
}

export function parseDistanceKm(distance) {
	if (distance == null || distance === "") return undefined;
	const value = String(distance).trim().toLowerCase();
	if (!value) return undefined;

	if (value.endsWith("km")) {
		const numeric = Number.parseFloat(value);
		return Number.isFinite(numeric) ? Number(numeric.toFixed(2)) : undefined;
	}

	if (value.endsWith("m")) {
		const numeric = Number.parseFloat(value);
		return Number.isFinite(numeric) ? Number((numeric / 1000).toFixed(2)) : undefined;
	}

	const numeric = Number.parseFloat(value);
	return Number.isFinite(numeric) ? Number(numeric.toFixed(2)) : undefined;
}

export function getInstitutionParams(institution = {}, extra = {}) {
	return cleanParams({
		institution_id: institution.id,
		institution_name: institution.name || institution.title,
		category_name: institution.type || institution.category_name,
		distance_km: institution.distanceKm ?? parseDistanceKm(institution.distance),
		rating: institution.rating,
		...extra,
	});
}

export function useServiceInstitutionTracking(deviceType) {
	const { locale } = useI18n();

	const baseParams = computed(() =>
		cleanParams({
			session_id: getSessionId(),
			user_id: getUserId(),
			login_status: getLoginStatus(),
			language: locale?.value === "en" ? "en" : "zh_hant",
			device_type: deviceType,
			page_name: PAGE_NAME,
			entry_source: getEntrySource(),
		})
	);

	function track(eventName, params = {}) {
		logEvent(eventName, cleanParams({ ...baseParams.value, ...params }));
	}

	return {
		track,
		baseParams,
	};
}
